/**
 * When an approved revision takes effect. These details belong to the
 * approval rather than to the decision to begin work.
 *
 * @module components/governance/ChangeCaseApplicability
 */

import React from "react";

import { EFFECTIVE_DATE_HELP } from "@/components/shared/InitialApprovalFields";
import { Heading, Text } from "@/components/ui";
import { FormField, TextField } from "@/components/ui/Form";
import type { CurriculumChangeCase } from "@/server/types";

export interface ChangeCaseApplicabilityProps {
  changeCase: CurriculumChangeCase;
  busy: boolean;
  effectiveFrom: string;
  onEffectiveFromChange: (value: string) => void;
}

export function ChangeCaseApplicability({
  changeCase,
  busy,
  effectiveFrom,
  onEffectiveFromChange,
}: ChangeCaseApplicabilityProps): React.ReactElement {
  return (
    <div className="grid gap-3" data-testid="change-case-applicability">
      <div>
        <Heading level={3}>Approved revision applicability</Heading>
        <Text tone="muted" className="mt-1">
          Set when this revision takes effect. These details are recorded with the approval, not
          when work begins.
        </Text>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <FormField
          label="Effective date"
          htmlFor="change-case-effective-from"
          helpText={EFFECTIVE_DATE_HELP}
          required
        >
          <TextField
            id="change-case-effective-from"
            type="date"
            value={effectiveFrom}
            onChange={onEffectiveFromChange}
            placeholder={changeCase.targetEffectiveFrom}
            disabled={busy}
            disabledReason="Wait for the change in progress to finish."
            data-testid="change-case-effective-from"
          />
        </FormField>
      </div>
    </div>
  );
}
